import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import DocumentAssessmentCard from './DocumentAssessmentCard';
import GapFillingWorkflow from './GapFillingWorkflow';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import {
  assessDocument,
  getDocumentAssessment,
  setDocumentProcessingMode,
  type DocumentAssessment
} from '../api';

interface UploadedDocument {
  id: number;
  original_name: string;
  file_type: string;
}

interface DocumentAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadedDocuments: UploadedDocument[];
  onComplete: () => void;
}

interface DocumentState {
  id: number;
  filename: string;
  assessment: DocumentAssessment | null;
  assessmentStatus: 'pending' | 'assessing' | 'completed' | 'failed';
  processingMode: 'preserve' | 'interpret';
  isProcessed: boolean;
}

const DocumentAssessmentModal: React.FC<DocumentAssessmentModalProps> = ({
  isOpen,
  onClose,
  uploadedDocuments,
  onComplete
}) => {
  const orchestratorCtx = useOrchestratorContext();
  const [documents, setDocuments] = useState<DocumentState[]>([]);
  const [currentStep, setCurrentStep] = useState<'assessment' | 'gap-filling' | 'complete'>('assessment');
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [showGapFilling, setShowGapFilling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && uploadedDocuments.length > 0) {
      initializeDocuments();
    }
  }, [isOpen, uploadedDocuments]);

  const initializeDocuments = async () => {
    // Initialize document states
    const initialStates: DocumentState[] = uploadedDocuments.map(doc => ({
      id: doc.id,
      filename: doc.original_name,
      assessment: null,
      assessmentStatus: 'pending',
      processingMode: 'interpret',
      isProcessed: false
    }));
    setDocuments(initialStates);

    // Start assessing documents
    for (let i = 0; i < initialStates.length; i++) {
      await assessSingleDocument(i, initialStates);
    }
  };

  const assessSingleDocument = async (index: number, currentDocs?: DocumentState[]) => {
    const docs = currentDocs || documents;
    const doc = docs[index];
    if (!doc) return;

    // Update status to assessing
    setDocuments(prev => prev.map((d, i) =>
      i === index ? { ...d, assessmentStatus: 'assessing' } : d
    ));

    try {
      // First check if already assessed
      const existingAssessment = await getDocumentAssessment(doc.id);

      if (existingAssessment.assessment) {
        setDocuments(prev => prev.map((d, i) =>
          i === index ? {
            ...d,
            assessment: existingAssessment.assessment,
            assessmentStatus: 'completed',
            processingMode: existingAssessment.processing_mode || 'interpret'
          } : d
        ));
      } else {
        // Run new assessment
        const result = await assessDocument(doc.id);
        setDocuments(prev => prev.map((d, i) =>
          i === index ? {
            ...d,
            assessment: result.assessment,
            assessmentStatus: 'completed',
            processingMode: result.assessment?.recommendation === 'preserve' ? 'preserve' : 'interpret'
          } : d
        ));
      }
    } catch (error) {
      console.error(`Error assessing document ${doc.id}:`, error);
      setDocuments(prev => prev.map((d, i) =>
        i === index ? { ...d, assessmentStatus: 'failed' } : d
      ));
    }
  };

  const handleModeChange = (docIndex: number, mode: 'preserve' | 'interpret') => {
    setDocuments(prev => prev.map((d, i) =>
      i === docIndex ? { ...d, processingMode: mode } : d
    ));
  };

  const handleContinue = async (docIndex: number) => {
    const doc = documents[docIndex];
    if (!doc) return;

    console.log('🔧 [ASSESSMENT] Saving processing mode for document:', {
      documentId: doc.id,
      filename: doc.filename,
      processingMode: doc.processingMode
    });

    try {
      setIsSubmitting(true);

      // Save processing mode to backend
      const result = await setDocumentProcessingMode(doc.id, doc.processingMode);
      console.log('✅ [ASSESSMENT] Processing mode saved successfully:', result);

      // Mark as processed
      setDocuments(prev => prev.map((d, i) =>
        i === docIndex ? { ...d, isProcessed: true } : d
      ));

      const gapsLength = doc.assessment?.completeness?.identified_gaps?.length ?? 0;
      if (doc.processingMode === 'preserve' && gapsLength > 0 && !orchestratorCtx.inRouter) {
        setShowGapFilling(true);
        setCurrentDocIndex(docIndex);
      } else {
        // Move to next document or complete
        moveToNext(docIndex);
      }
    } catch (error) {
      console.error('Error setting processing mode:', error);
      toast.error('Failed to save processing mode');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGapFillingComplete = () => {
    setShowGapFilling(false);
    moveToNext(currentDocIndex);
  };

  const handleGapFillingCancel = () => {
    setShowGapFilling(false);
    moveToNext(currentDocIndex);
  };

  const moveToNext = (currentIndex: number) => {
    const nextUnprocessed = documents.findIndex((d, i) => i > currentIndex && !d.isProcessed);

    if (nextUnprocessed >= 0) {
      setCurrentDocIndex(nextUnprocessed);
    } else {
      // All done
      setCurrentStep('complete');
    }
  };

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  // Show gap filling workflow
  if (showGapFilling && documents[currentDocIndex]) {
    return (
      <GapFillingWorkflow
        documentId={documents[currentDocIndex].id}
        onComplete={handleGapFillingComplete}
        onCancel={handleGapFillingCancel}
      />
    );
  }

  // Show completion screen
  if (currentStep === 'complete') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Assessment Complete</h2>
          <p className="text-gray-600 mb-6">
            All documents have been assessed and configured for processing.
          </p>
          <div className="space-y-2 mb-6">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm text-gray-700 truncate">{doc.filename}</span>
                <span className={`text-xs px-2 py-1 rounded ${
                  doc.processingMode === 'preserve'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {doc.processingMode === 'preserve' ? 'Preserve' : 'Interpret'}
                </span>
              </div>
            ))}
          </div>
          <button
            onClick={handleComplete}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Continue to Analysis
          </button>
        </div>
      </div>
    );
  }

  // Show assessment cards
  const unprocessedDocs = documents.filter(d => !d.isProcessed);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Document Assessment</h2>
            <p className="text-sm text-gray-500">
              Choose how to process each uploaded document
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-4 py-3 bg-gray-50 border-b">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Processing {documents.filter(d => d.isProcessed).length + 1} of {documents.length} documents
            </span>
          </div>
        </div>

        {/* Document Cards */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {unprocessedDocs.map((doc, index) => {
            const realIndex = documents.findIndex(d => d.id === doc.id);
            return (
              <DocumentAssessmentCard
                key={doc.id}
                documentId={doc.id}
                filename={doc.filename}
                assessment={doc.assessment}
                assessmentStatus={doc.assessmentStatus}
                processingMode={doc.processingMode}
                onModeChange={(mode) => handleModeChange(realIndex, mode)}
                onContinue={() => handleContinue(realIndex)}
                isLoading={isSubmitting}
              />
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Skip Assessment
          </button>
          <div className="text-sm text-gray-500">
            {documents.filter(d => d.assessmentStatus === 'completed').length} of {documents.length} assessed
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentAssessmentModal;
